import { useCallback, useEffect, useState } from 'react'
import { ApiClientError, type Page, type Pipeline } from '../api/types.js'
import { Alert } from '../ui/Alert.js'
import { ConfirmDialog, useConfirmation } from '../ui/ConfirmDialog.js'
import { formatMoment } from '../ui/moment.js'
import { вКавычках } from '../ui/quotes.js'
import { useToaster } from '../ui/toasts.js'

/**
 * Узкий срез клиента: экрану нужен только список сценариев и создание
 * нового (барьер 2 — клиент API и есть граница процесса для экранов
 * админки, см. Instances.tsx).
 */
export interface PipelinesClient {
  pipelines(): Promise<Page<Pipeline>>
  createPipeline(name: string, description?: string): Promise<Pipeline>
  removePipeline(id: string): Promise<void>
}

export interface PipelinesProps {
  client: PipelinesClient
  /** Права, выданные при входе (session.permissions()). */
  permissions: string[]
}

const CAN_WRITE = 'pipelines.write'

const GENERIC_FAILURE = 'Не удалось выполнить действие. Проверьте соединение и попробуйте ещё раз.'

/**
 * Состояния сценария человеческими словами. `broken` — не «ошибка», а
 * состояние: опубликованный сценарий, чью ноду или credential убрали из-под
 * него, дальше не запускается (FR-PIPE-016).
 */
const STATUS_LABELS: Record<string, string> = {
  draft: 'Черновик',
  published: 'Опубликован',
  disabled: 'Выключен',
  broken: 'Сломан',
}

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status
}

function messageOf(cause: unknown): string {
  return cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE
}

/**
 * Список сценариев арендатора. Открывает редактор: сам редактор живёт по
 * адресу `/app/pipelines/:id`, и попасть в него иначе как отсюда нельзя —
 * идентификатор сценария человеку неизвестен.
 */
export function Pipelines(props: PipelinesProps) {
  const { client, permissions } = props
  const canWrite = permissions.includes(CAN_WRITE)
  const toaster = useToaster()
  const удаление = useConfirmation<Pipeline>()

  const [pipelines, setPipelines] = useState<Pipeline[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [removing, setRemoving] = useState<ReadonlySet<string>>(new Set())
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)

  const load = useCallback(() => {
    let cancelled = false
    client
      .pipelines()
      .then((page) => {
        if (cancelled) return
        setPipelines(page.items)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(messageOf(cause))
      })
    return () => {
      cancelled = true
    }
  }, [client])

  useEffect(load, [load])

  async function create() {
    setCreating(true)
    setError(null)
    try {
      const created = await client.createPipeline(name)
      // Новый сценарий приписывается к списку, а не перечитывается
      // страницей: перечитать — потерять место в списке, если он длинный.
      setPipelines((current) => [created, ...(current ?? [])])
      setShowForm(false)
      setName('')
      toaster.succeeded(`Сценарий ${вКавычках(created.name)} создан`)
    } catch (cause: unknown) {
      setError(messageOf(cause))
    } finally {
      setCreating(false)
    }
  }

  /**
   * Удаление сценария уносит его версии и журнал прогонов — это сказано
   * прямо в подтверждении, а не выясняется после. Отказ службы (идёт
   * прогон, сценарий назначен обработчиком падений) приходит с причиной и
   * показывается как есть: она объясняет, что сделать, чтобы удалить.
   */
  async function remove(pipeline: Pipeline) {
    удаление.cancel()
    setRemoving((current) => new Set(current).add(pipeline.id))
    setError(null)
    try {
      await client.removePipeline(pipeline.id)
      setPipelines((items) => (items ?? []).filter((item) => item.id !== pipeline.id))
      // Строка просто исчезает, и без слов это читается как «не туда
      // нажал»: подтверждение называет то, чего больше нет.
      toaster.succeeded(`Сценарий ${вКавычках(pipeline.name)} удалён`)
    } catch (cause: unknown) {
      setError(messageOf(cause))
    } finally {
      setRemoving((current) => {
        const next = new Set(current)
        next.delete(pipeline.id)
        return next
      })
    }
  }

  return (
    <section aria-label="Сценарии">
      {error !== null && <Alert>{error}</Alert>}

      <div className="панель-раздела">
        <p className="панель-раздела__итог">
          {pipelines === null
            ? 'Загружаем сценарии…'
            : pipelines.length === 0
              ? 'Ни одного сценария не собрано'
              : `Сценариев: ${pipelines.length}`}
        </p>
        {canWrite && (
          <button type="button" className="btn btn--primary" onClick={() => setShowForm(!showForm)}>
            Новый сценарий
          </button>
        )}
      </div>

      {showForm && (
        <form
          className="card"
          onSubmit={(event) => {
            event.preventDefault()
            void create()
          }}
        >
          <label htmlFor="сценарий-имя">Название</label>
          <input
            id="сценарий-имя"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
          <button type="submit" className="btn" disabled={creating || name.trim() === ''}>
            Создать
          </button>
        </form>
      )}

      {pipelines === null && error === null && <p>Загрузка сценариев…</p>}

      {pipelines !== null && pipelines.length === 0 && (
        <p>
          Сценариев пока нет. Сценарий отвечает на сообщения вместо человека: пришло сообщение —
          он решает, что с ним делать, и делает. Соберите первый: нужен триггер (например,
          входящее сообщение) и хотя бы один шаг после него.
        </p>
      )}

      {pipelines !== null && pipelines.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Название</th>
              <th scope="col">Состояние</th>
              <th scope="col">Изменён</th>
              {canWrite && <th scope="col">Действия</th>}
            </tr>
          </thead>
          <tbody>
            {pipelines.map((pipeline) => (
              <tr key={pipeline.id}>
                <td>
                  <a href={`/app/pipelines/${pipeline.id}`}>{pipeline.name}</a>
                  {pipeline.description !== null && (
                    <span className="table__hint">{pipeline.description}</span>
                  )}
                </td>
                <td>
                  {statusLabel(pipeline.status)}
                  {/* Причина поломки показывается рядом с состоянием, а не
                      прячется в подсказку: «Сломан» без причины не говорит,
                      что делать (FR-PIPE-016). */}
                  {pipeline.brokenReason !== null && (
                    <span className="table__hint">{pipeline.brokenReason}</span>
                  )}
                </td>
                <td>{formatMoment(pipeline.updatedAt)}</td>
                {canWrite && (
                  <td>
                    <button
                      type="button"
                      className="btn btn--danger"
                      disabled={removing.has(pipeline.id)}
                      onClick={() => удаление.ask(pipeline)}
                    >
                      Удалить
                    </button>
                    <span className="table__hint">Вместе с версиями и журналом прогонов</span>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {удаление.pending !== null && (
        <ConfirmDialog
          title={`Удалить сценарий ${вКавычках(удаление.pending.name)}?`}
          confirmLabel="Удалить"
          busy={removing.has(удаление.pending.id)}
          onCancel={удаление.cancel}
          onConfirm={() => {
            if (удаление.pending !== null) void remove(удаление.pending)
          }}
        >
          <p>
            Вместе со сценарием исчезнут все его версии и журнал прогонов — чем закончился
            каждый запуск и на каком шаге он падал.
          </p>
          <p>Это нельзя отменить.</p>
        </ConfirmDialog>
      )}
    </section>
  )
}
